import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

import { useSupabase } from './use-supabase';

type SignedUrlState = {
  url: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Parse a full storage path to extract bucket name and path within bucket.
 *
 * Storage paths from FileUploader are in format: `bucket-name/path/to/file.jpg`
 *
 * @example
 * parseStoragePath('identity-documents/user-id/dancer-identity/CH/file.jpg')
 * // Returns { bucket: 'identity-documents', path: 'user-id/dancer-identity/CH/file.jpg' }
 */
export function parseStoragePath(fullPath: string): {
  bucket: string;
  path: string;
} | null {
  if (!fullPath || !fullPath.includes('/')) {
    return null;
  }

  const firstSlash = fullPath.indexOf('/');
  const bucket = fullPath.substring(0, firstSlash);
  const path = fullPath.substring(firstSlash + 1);

  if (!bucket || !path) {
    return null;
  }

  return { bucket, path };
}

/**
 * Hook to generate a signed URL from a Supabase Storage path.
 *
 * For private buckets (like identity-documents), images cannot be displayed
 * directly using the storage path. This hook generates a temporary signed URL
 * that can be used as an img src.
 *
 * @param storagePath - Full storage path including bucket name (e.g., "identity-documents/user-id/file.jpg")
 * @param expiresIn - URL expiration time in seconds (default: 3600 = 1 hour)
 *
 * @example
 * ```tsx
 * const { url, loading, error } = useStorageSignedUrl(document.frontImageUrl);
 *
 * if (loading) return <Spinner />;
 * if (url) return <img src={url} alt="Document" />;
 * ```
 */
export function useStorageSignedUrl(
  storagePath: string | null | undefined,
  expiresIn: number = 3600,
): SignedUrlState {
  const supabase = useSupabase();
  const [state, setState] = useState<SignedUrlState>({
    url: null,
    loading: !!storagePath,
    error: null,
  });

  useEffect(() => {
    if (!storagePath) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset state when path is cleared
      setState({ url: null, loading: false, error: null });
      return;
    }

    const parsed = parseStoragePath(storagePath);
    if (!parsed) {
      setState({ url: null, loading: false, error: 'Invalid storage path' });
      return;
    }

    let cancelled = false;

    const generateUrl = async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const { data, error } = await supabase.storage
          .from(parsed.bucket)
          .createSignedUrl(parsed.path, expiresIn);

        if (cancelled) return;

        if (error) {
          setState({ url: null, loading: false, error: error.message });
          return;
        }

        setState({ url: data.signedUrl, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          url: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to generate URL',
        });
      }
    };

    generateUrl();

    return () => {
      cancelled = true;
    };
  }, [storagePath, expiresIn, supabase]);

  return state;
}

/**
 * Generate a signed URL synchronously (for use outside React components).
 *
 * @param client - Supabase client
 * @param storagePath - Full storage path including bucket name
 * @param expiresIn - URL expiration time in seconds
 */
export async function getStorageSignedUrl(
  client: SupabaseClient,
  storagePath: string,
  expiresIn: number = 3600,
): Promise<string | null> {
  const parsed = parseStoragePath(storagePath);
  if (!parsed) return null;

  const { data, error } = await client.storage
    .from(parsed.bucket)
    .createSignedUrl(parsed.path, expiresIn);

  if (error || !data?.signedUrl) {
    return null;
  }

  return data.signedUrl;
}
